<template>
  <header class="bg-white shadow-sm border-b border-gray-200">
    <nav class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <div class="flex items-center">
          <RouterLink to="/" class="flex items-center space-x-2">
            <div class="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span class="text-white font-bold text-sm">CC</span>
            </div>
            <span class="font-semibold text-gray-900">Calmren</span>
          </RouterLink>
        </div>

        <!-- Desktop Navigation -->
        <div class="hidden md:flex items-center space-x-6">
          <template v-for="item in navigation" :key="item.name">
            <!-- Logout Button -->
            <button
              v-if="item.action === 'logout'"
              @click="handleLogout"
              class="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors"
            >
              {{ item.name }}
            </button>

            <!-- Dropdown Menu -->
            <div
              v-else-if="item.dropdown"
              class="relative"
              @mouseenter="activeDropdown = item.name"
              @mouseleave="activeDropdown = null"
            >
              <RouterLink
                :to="item.href"
                class="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors flex items-center"
                :class="{ 'text-primary-600': $route.path === item.href || item.dropdown.some(sub => $route.path === sub.href) }"
              >
                {{ item.name }}
                <svg class="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </RouterLink>

              <!-- Dropdown Content -->
              <div
                v-show="activeDropdown === item.name"
                class="absolute left-0 mt-1 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50"
              >
                <div class="py-1">
                  <RouterLink
                    v-for="subItem in item.dropdown"
                    :key="subItem.name"
                    :to="subItem.href"
                    class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    :class="{ 'bg-gray-50 text-primary-600': $route.path === subItem.href }"
                  >
                    <span class="mr-3">{{ subItem.icon }}</span>
                    {{ subItem.name }}
                  </RouterLink>
                </div>
              </div>
            </div>

            <!-- Regular Link -->
            <RouterLink
              v-else
              :to="item.href"
              class="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors"
              :class="{ 'text-primary-600': $route.path === item.href }"
            >
              {{ item.name }}
            </RouterLink>
          </template>
        </div>

        <!-- Mobile menu button -->
        <div class="md:hidden flex items-center">
          <button
            @click="mobileMenuOpen = !mobileMenuOpen"
            class="text-gray-600 hover:text-gray-900 p-2"
          >
            <Bars3Icon v-if="!mobileMenuOpen" class="h-6 w-6" />
            <XMarkIcon v-else class="h-6 w-6" />
          </button>
        </div>
      </div>

      <!-- Mobile Navigation -->
      <div v-if="mobileMenuOpen" class="md:hidden py-4 border-t border-gray-200">
        <div class="space-y-1">
          <template v-for="item in navigation" :key="item.name">
            <!-- Logout Button -->
            <button
              v-if="item.action === 'logout'"
              @click="handleLogout"
              class="block w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900"
            >
              {{ item.name }}
            </button>

            <!-- Dropdown Section -->
            <div v-else-if="item.dropdown" class="space-y-1">
              <div class="px-3 py-2 text-base font-medium text-gray-900 bg-gray-50">
                {{ item.name }}
              </div>
              <RouterLink
                v-for="subItem in item.dropdown"
                :key="subItem.name"
                :to="subItem.href"
                @click="mobileMenuOpen = false"
                class="block pl-6 pr-3 py-2 text-sm text-gray-600 hover:text-gray-900 flex items-center"
                :class="{ 'text-primary-600': $route.path === subItem.href }"
              >
                <span class="mr-3">{{ subItem.icon }}</span>
                {{ subItem.name }}
              </RouterLink>
            </div>

            <!-- Regular Link -->
            <RouterLink
              v-else
              :to="item.href"
              @click="mobileMenuOpen = false"
              class="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900"
              :class="{ 'text-primary-600': $route.path === item.href }"
            >
              {{ item.name }}
            </RouterLink>
          </template>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { Bars3Icon, XMarkIcon } from '@heroicons/vue/24/outline'
import { authService } from '@/services/auth'

const router = useRouter()
const mobileMenuOpen = ref(false)
const activeDropdown = ref(null)

// Check if user is authenticated
const isAuthenticated = ref(false)

// Check auth status on component mount
onMounted(async () => {
  // Check authentication with Cloudflare Access
  const user = await authService.checkAuth()
  isAuthenticated.value = !!user
})

// Public navigation (always visible)
const publicNavigation = [
  { name: 'Home', href: '/' },
  { name: 'Contact', href: '/contact' },
]

// Business navigation with dropdown structure
const businessNavigation = [
  { name: '📊 Dashboard', href: '/dashboard' },
  { name: '⏱️ Time Log', href: '/time-log' },
  {
    name: '💰 Financial',
    href: '/expense-entry',
    dropdown: [
      { name: 'Expense Entry', href: '/expense-entry', icon: '💰' },
      { name: 'Expense Sheet', href: '/expense-sheet', icon: '📊' },
      { name: 'Tax Calculator', href: '/tax-calculator', icon: '🧮' },
      { name: 'Business Settings', href: '/business-calculations', icon: '⚙️' },
    ]
  },
  {
    name: '👥 Clients',
    href: '/clients',
    dropdown: [
      { name: 'Client Management', href: '/clients', icon: '👥' },
      { name: 'Invoices', href: '/invoices', icon: '📄' },
    ]
  },
  { name: '🎯 FSU Projects', href: '/fsu-projects' },
]

// Combined navigation based on auth status
const navigation = computed(() => {
  if (isAuthenticated.value) {
    return [...publicNavigation, ...businessNavigation, { name: '🚪 Logout', href: '#', action: 'logout' }]
  }
  // No login link - Cloudflare Access handles login automatically
  return publicNavigation
})

// Handle logout
const handleLogout = async () => {
  // Cloudflare Access logout will redirect and clear session
  await authService.logout()
}
</script>
